import { DateArg, format, isMatch, isValid, parseISO } from 'date-fns'
import { DateInput, DurationInput, normalize } from 'duration-fns'
import i18n, { TFunction } from 'i18next'
import { isString } from 'lodash-es'
import memoizeOne from 'memoize-one'
import moment from 'moment'

import { User } from '@/types/user'

/** Use for time of day comparison as this function creates a new Date in 1970 with the time copied from the source date */
export function normalizeTimeOfDay<DateType extends Date>(date: DateArg<DateType>): Date {
	const dateObj = new Date(isString(date) ? toLocalDate(date) : date)
	return new Date(1970, 0, 1, dateObj.getHours(), dateObj.getMinutes(), dateObj.getSeconds(), dateObj.getMilliseconds())
}

export const timeFormatter = new Intl.DateTimeFormat('en-GB', {
	hour: 'numeric',
	minute: 'numeric',
	hour12: false,
})

/**
 * Time formats for data transformation only, not for displaying to the user because these are not multi locale.
 * @see {@link TimeFormatKeys} and {@link MultiLocaleTimeFormats} for time formats that are safe to be displayed.
 */
export const TimeFormatsLocaleUnsafe = {
	'24HoursWithSeconds': 'HH:mm:ss',
	'24Hours': 'HH:mm',
	DateOnly: 'YYYY-MM-DD',
	FullDateAndTime: 'YYYY-MM-DD[T]HH:mm',
}

export const getTimeFormat = (t: TFunction) => ({
	/**	En key: `ddd, MMM D`, En result: `Tue, Mar 17` for date: 17-03-2020 */
	DayNameAndDayMonthName: t('date-time-formats.day-name-and-day-month-name'),
	/**	En key: `MMM D`, En result: `Mar 17` for date: 17-03-2020 */
	DayAndMonthName: t('date-time-formats.day-and-month-name'),
	/**  En key: `MMM D LT`, En result: `Mar 17 12:30 PM` for date 17-03-2020 */
	MonthNameDayAndTime: t('date-time-formats.month-name-day-and-hour'),
	/**  En key: `DD/MM`, En result: `17/03` for date 17-03-2020 */
	DayAndMonth: t('date-time-formats.day-and-month'),
	/**  En key: `MMM DD, YYYY, HH:mm`, En result: `Mar 17, 2020, 12:30 PM, ` for date 17-03-2020 */
	DateShortMonthYearAndTime: 'MMM DD, YYYY, hh:mm A',
})

/**
 * Time formats that support all locales, you can safely use them to display time and dates to the user.
 */
export const MultiLocaleTimeFormats = {
	/** En result: `8:30 PM` */
	Time: 'LT',
	/** En result: `8:30:25 PM` */
	TimeWithSeconds: 'LTS',
	/** En result: `09/04/1986` */
	DateZeroPadding: 'L',
	/** En result: `9/4/1986` */
	Date: 'l',
	/** En result: `September 4, 1986` */
	FullReadableDate: 'LL',
	/** En result: `Sep 4, 1986` */
	FullReadableDateShortMonth: 'll',
	/** En result: `Thursday, September 4, 1986 8:30 PM` */
	FullReadableDateWithTime: 'LLLL',
	/** En result: `Thu, Sep 4, 1986 8:30 PM` */
	FullReadableDateWithTimeShort: 'llll',
	/** En result: `Thursday` */
	DayName: 'dddd',
	/** En result: `September` */
	MonthName: 'MMMM',
	/** En result: `09/20` */
	CreditCardExpiration: 'MM / YY',
	/** En result: 22 Jul 2021, 19:00 */
	FullReadableDateShortMonthWithTime: 'DD MMM YYYY, LT',
	/** En result: Jul 31, 2023, 2:00 AM */
	FullReadableDateMonthFirstWithTime: 'MMM DD, YYYY, LT',
}

export class TimeObject {
	public hours: number
	public minutes: number

	constructor(hours: number, minutes: number) {
		this.hours = hours
		this.minutes = minutes
	}

	public get h() {
		return this.hours
	}

	public get hour() {
		return this.hours
	}

	public get m() {
		return this.minutes
	}

	public get minute() {
		return this.minutes
	}

	static fromString(time?: string | TimeObject | null): TimeObject | undefined {
		if (!time) {
			return undefined
		}

		if (this.isTimeObject(time)) {
			return time
		}

		if (time.length < 5 || !time.includes(':')) {
			throw new Error('time string is invalid')
		}

		const d = moment(time, MultiLocaleTimeFormats.Time)
		return new TimeObject(d.get('hours'), d.get('minutes'))
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	static isTimeObject(time: any): time is TimeObject {
		return time instanceof TimeObject
	}

	public toTimeValueString() {
		const date = new Date()
		date.setHours(this.hours)
		date.setMinutes(this.minutes)
		return timeFormatter.format(date)
	}

	/** Returns a string representation of this object. */
	public toString() {
		return moment(this).format(MultiLocaleTimeFormats.Time)
	}

	public toJSON() {
		return this.toString()
	}
}

export function getTimeObjectFromString(time?: string) {
	return time ? TimeObject.fromString(time) : undefined
}

export function getTotalFromTimeObject(timeObject?: TimeObject) {
	return timeObject ? timeObject.h * 60 + timeObject.m : undefined
}

export function compareTimes(timeA: string, timeB: string) {
	const minutesDiff =
		getTotalFromTimeObject(getTimeObjectFromString(timeA)!)! - getTotalFromTimeObject(getTimeObjectFromString(timeB)!)!
	return minutesDiff === 0 ? 0 : minutesDiff > 0 ? 1 : -1
}

const timezonePatterns = [
	/([+-])(\d{2})(:?(\d{2}))?$/, // ±hh, ±hhmm or ±hh:mm
	/(Z)$/, // Z
	/(UTC|(?:[a-zA-Z]+\/[a-zA-Z_]+(?:\/[a-zA-Z_]+)?))$/, // IANA time zone
]

/**
 * Use to display local date and time
 *
 * For example:
 * - `2025-02-17` becomes `2025-02-17T00:00`
 * - `2025-02-17T17:19:00-05:00` becomes `2025-02-17T17:19:00`
 */
export function toLocalDate(date: string) {
	// Add time
	// new Date('2025-04-02') -> Wed Apr 02 2025 02:00:00 GMT+0200
	// new Date('2025-04-02T00:00') -> Wed Apr 02 2025 00:00:00 GMT+0200
	if (!date.includes('T') && isMatch(date, 'yyyy-MM-dd')) {
		return date + 'T00:00'
	}

	// Remove timezone
	for (const pattern of timezonePatterns) {
		const localDate = date.replace(pattern, '')
		if (localDate !== date) {
			return localDate
		}
	}

	return date
}

/** Format: `yyyy-MM-dd` */
export function formatDateOnly(date: DateInput) {
	return format(date, 'yyyy-MM-dd')
}

/** Format: `yyyy-MM-dd'T'HH:mm` */
export function formatDateTime(date: DateInput) {
	return format(date, "yyyy-MM-dd'T'HH:mm")
}

/** Format: `yyyy-MM-dd'T'HH:mm':00.000Z'` */
export function formatUTC(date: DateInput) {
	return format(date, "yyyy-MM-dd'T'HH:mm':00.000Z'")
}

type DateTimeFormat =
	| 'shortMonthDay'
	| 'numericMonthDay'
	| 'shortMonthWeekday'
	| 'yearShortMonthDay'
	| 'yearShortMonthTime'

const DateTimeFormats: {
	[format in DateTimeFormat]: Intl.DateTimeFormatOptions
} = {
	// https://github.com/date-fns/date-fns/pull/3990 will replace `date without year` formatters
	/**
	 * Looks like `Jun 15`
	 * E.g. departure/arrival date
	 */
	shortMonthDay: {
		month: 'short',
		day: 'numeric',
	},

	/**
	 * Looks like `03/15`
	 * E.g. departure/arrival date
	 */
	numericMonthDay: {
		month: 'numeric',
		day: 'numeric',
	},

	/**
	 * Looks like `Sat, Mar 15`
	 * E.g. departure/arrival date
	 */
	shortMonthWeekday: {
		month: 'short',
		day: 'numeric',
		weekday: 'short',
	},

	/**
	 * Looks like `Mar 20, 2025`
	 * E.g. Expenses with different locales
	 */
	yearShortMonthDay: {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	},

	/**
	 * Looks like `Mar 20, 2025, 07:20 PM`
	 * E.g. Expenses with different locales
	 */
	yearShortMonthTime: {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	},
} as const

const localeDateTimeFormats: Partial<Record<string, Partial<Record<DateTimeFormat, Intl.DateTimeFormat['format']>>>> =
	{}

const getLocaleDateTimeFormat = (locale: string, format: DateTimeFormat) => {
	if (localeDateTimeFormats[locale]?.[format]) {
		return localeDateTimeFormats[locale][format]
	}

	if (!localeDateTimeFormats[locale]) {
		localeDateTimeFormats[locale] = {}
	}

	return (localeDateTimeFormats[locale][format] = new Intl.DateTimeFormat(locale, DateTimeFormats[format]).format)
}

/** Format date in {@link DateTimeFormats} */
export function formatDate(date: DateInput, formatKey: DateTimeFormat, locale?: string) {
	return getLocaleDateTimeFormat(locale ?? i18n.language, formatKey)(typeof date === 'string' ? new Date(date) : date)
}

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType extends readonly (infer ElementType)[]
	? ElementType
	: never

const durationUnits = {
	year: 'years',
	month: 'months',
	week: 'weeks',
	day: 'days',
	hour: 'hours',
	minute: 'minutes',
} as const

const getLocaleUnitNarrowFormats = memoizeOne((locale: string) => {
	const units = Object.keys(durationUnits) as [keyof typeof durationUnits]
	return units.reduce(
		(formats, unit) => {
			try {
				return {
					...formats,
					[unit]: new Intl.NumberFormat(locale, {
						style: 'unit',
						unit,
						unitDisplay: 'narrow',
					}).format,
				}
			} catch (err) {
				return {
					...formats,
					[unit]: (value: number) => `${value}${unit[0]}`,
				}
			}
		},
		{} as Record<ArrayElement<typeof units>, Intl.NumberFormat['format']>,
	)
})

/** Looks like `6d 1h 5m` */
export const formatDurationNarrow = (duration: DurationInput, referenceDate?: DateInput) => {
	const d = normalize(duration, referenceDate)
	const formats = getLocaleUnitNarrowFormats(i18n.language)
	return (
		(['year', 'month', 'week', 'day', 'hour', 'minute'] as const)
			.reduce((result, key) => {
				const k = durationUnits[key]
				d[k] && result.push(formats[key](d[k]))
				return result
			}, [] as string[])
			.join(' ') || formats.minute(0)
	)
}

export function formatUserDate(date: Date | string, user: User) {
	return formatDate(date, 'yearShortMonthDay', user.language_code || undefined)
}

export function smartDateRange(
	start: moment.MomentInput,
	end: moment.MomentInput,
	options?: {
		includeYear: boolean
	},
): string[] {
	const curYear = new Date().getFullYear()
	const from = moment(start)
	const to = moment(end)

	const includeMonth = from.month() !== to.month() || from.year() !== to.year()
	const includeYear =
		options?.includeYear || from.year() !== to.year() || from.year() !== curYear || to.year() !== curYear
	const isSameYear = from.year() === to.year()
	let fromFormat = 'MMM D'
	let toFormat = 'D'
	if (includeMonth) {
		toFormat = 'MMM ' + toFormat
	}
	if (includeYear) {
		if (!isSameYear) {
			fromFormat += ', YYYY'
		}
		toFormat += ', YYYY'
	}

	return [from.format(fromFormat), to.format(toFormat)]
}

export function getSmartDate(startDate: Date, endDate: Date) {
	return smartDateRange(startDate, endDate, { includeYear: true }).join(' – ')
}

export function getFormattedDateRange(
	startTime: moment.MomentInput,
	endTime: moment.MomentInput,
	longFormat = MultiLocaleTimeFormats.FullReadableDateShortMonth,
): string[] {
	return [moment(startTime).format(longFormat), moment(endTime).format(longFormat)]
}

export function formatComplimentaryPeriodText({ rawDate, t }: { rawDate: string | null; t: TFunction }) {
	if (!rawDate) {
		return null
	}

	const date = new Date(rawDate)

	if (Number.isNaN(date.getTime())) {
		return null
	}

	return t('Your complimentary period ends on {{date}}.', {
		date: `${formatDate(date, 'yearShortMonthTime')} (CET Time)`,
	})
}

export const formatDisplayDateTime = (value?: string) => {
	if (!value) {
		return ''
	}

	const date = parseISO(value)

	return isValid(date) ? format(date, 'PPp') : ''
}

export const duration = (from: string, to: string) => {
	return new Date(to).getTime() - new Date(from).getTime()
}
